import { useEffect, useRef, useState } from "react"
import { Bookmark as BookmarkIcon, Loader2 } from "lucide-react"

import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { redeemSaveLink } from "@/lib/openHouse/guest"
import { GUEST } from "@/lib/openHouse/wording"
import { cn } from "@/lib/utils"
import { ErrorLine } from "./UploadControl"

// Bookmark (spec P3-H5; BRIEF-2 step 4; guest-wording §1): the optional "save
// by email" dialog over any guest step, a bottom sheet on a phone. Saving is
// never required, and the one-time reminder consent is its own checkbox,
// unchecked unless this draft already has it.
//
// onSend({ email, reminderConsent, requestKey }) saves the draft and emails
// the link; it rejects with `stage: "save"` when the draft did not save, or the
// server's refusal. A retry resends the same request (its requestKey), so it
// cannot send a second email; a changed address is a new request.

// The same Privacy Policy the sign-up pages link to.
const PRIVACY_URL = "https://theurbanwriters.com/pages/privacy-policy"
const EMAIL = /^[^@\s]+@[^@\s]+\.[^@\s]+$/

const newRequestKey = () =>
  Array.from(window.crypto.getRandomValues(new Uint8Array(16)), (b) => b.toString(16).padStart(2, "0")).join("")

const RETURN_PATH = "/post-project/return"

// On a save link's visit (/post-project/return#<token>), the redemption of its
// token, taken once from the layout's head script (shared/_fragment_handoff),
// which cleared it from the address before any analytics tag loaded; otherwise
// null. A visit with no token is an invalid link.
export function openReturnLink() {
  if (window.location.pathname.replace(/\/$/, "") !== RETURN_PATH) return null // the server routes /post-project/return/ here too
  const take = window.__tuwFragment
  const token = typeof take === "function" ? take() : ""
  window.history.replaceState(null, "", "/post-project")
  if (token) return redeemSaveLink(token)
  return Promise.reject(Object.assign(new Error("link_invalid"), { reason: "link_invalid" }))
}

// What a save link that can't open its draft shows, by the server's reason:
// heading, then body lines. An expired draft has its own page, and a posted
// one leaves for its project (openPostedProject).
export function linkPage(reason) {
  if (reason === "link_expired") return [GUEST.linkExpiredHeading, GUEST.linkExpiredBody, GUEST.linkRecovery]
  if (reason === "link_invalid") return [GUEST.linkInvalidHeading, GUEST.linkInvalidBody, GUEST.linkRecovery]
  return [GUEST.accessUnavailable]
}

export function MessagePage({ lines: [heading, ...body], onStart }) {
  return (
    <div className="mx-auto flex max-w-[560px] flex-col items-start gap-3 px-8 pt-12 max-[561px]:px-4">
      <h1 className="font-display text-[22px] font-bold tracking-tight text-foreground">{heading}</h1>
      {body.map((line) => <p key={line} className="text-[13.5px] leading-relaxed text-muted-foreground">{line}</p>)}
      <Button onClick={onStart}>{GUEST.startNew}</Button>
    </div>
  )
}

// The way into the dialog: an outlined button under every step's Back, and in
// the price box on a desktop.
export function SaveLaterButton({ onClick, className }) {
  return (
    <Button type="button" variant="outline" onClick={onClick} className={cn("gap-1.5 font-semibold", className)}>
      <BookmarkIcon className="size-4" strokeWidth={2} />
      {GUEST.bookmarkHeading}
    </Button>
  )
}

const SHEET = "max-[561px]:top-auto max-[561px]:bottom-0 max-[561px]:max-w-full max-[561px]:translate-y-0 max-[561px]:rounded-b-none"

export function Bookmark({ open, onOpenChange, days, saved = {}, onSend }) {
  const [email, setEmail] = useState("")
  const [consent, setConsent] = useState(false)
  const [showErr, setShowErr] = useState(false)
  const [status, setStatus] = useState("form") // form | sending | saved | deliveryFailed | saveFailed | throttled
  const request = useRef(null)

  useEffect(() => {
    if (!open) return
    setEmail(saved.email || "")
    setConsent(!!saved.reminderConsent)
    setShowErr(false)
    setStatus("form")
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const issue = !email.trim() ? GUEST.emailRequired : !EMAIL.test(email.trim()) ? GUEST.emailInvalid : null

  const send = async () => {
    setStatus("sending")
    try {
      await onSend(request.current)
      setStatus("saved")
    } catch (err) {
      if (err?.reason === "email_invalid") {
        setShowErr(true)
        return setStatus("form")
      }
      setStatus(err?.stage === "save" ? "saveFailed" : err?.status === 429 ? "throttled" : "deliveryFailed")
    }
  }

  const submit = (event) => {
    event.preventDefault()
    setShowErr(true)
    if (issue) return
    request.current = { email: email.trim(), reminderConsent: consent, requestKey: newRequestKey() }
    send()
  }

  const changeEmail = () => {
    setShowErr(false)
    setStatus("form")
  }

  let body
  if (status === "saved") {
    body = (
      <>
        <DialogHeader>
          <DialogTitle>{GUEST.bookmarkSavedHeading}</DialogTitle>
          <DialogDescription>{GUEST.bookmarkSavedBody(request.current.email)}</DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={changeEmail}>{GUEST.changeEmail}</Button>
          <Button onClick={() => onOpenChange(false)}>{GUEST.bookmarkContinue}</Button>
        </DialogFooter>
      </>
    )
  } else if (["deliveryFailed", "saveFailed", "throttled"].includes(status)) {
    const failed = {
      deliveryFailed: [GUEST.deliveryFailed, GUEST.deliveryRetry],
      saveFailed: [GUEST.saveFailed, GUEST.saveRetry],
      throttled: [GUEST.bookmarkThrottled, null],
    }[status]
    body = (
      <>
        <DialogHeader>
          <DialogTitle>{GUEST.bookmarkHeading}</DialogTitle>
        </DialogHeader>
        <ErrorLine>{failed[0]}</ErrorLine>
        <DialogFooter>
          <Button variant="outline" onClick={changeEmail}>{GUEST.changeEmail}</Button>
          {failed[1] && <Button onClick={send}>{failed[1]}</Button>}
        </DialogFooter>
      </>
    )
  } else {
    const sending = status === "sending"
    body = (
      <form noValidate onSubmit={submit} className="flex flex-col gap-4">
        <DialogHeader>
          <DialogTitle>{GUEST.bookmarkHeading}</DialogTitle>
          <DialogDescription>{GUEST.bookmarkBody}</DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="bookmark-email">{GUEST.emailLabel}</Label>
          <Input
            id="bookmark-email"
            type="email"
            autoComplete="email"
            value={email}
            disabled={sending}
            aria-invalid={showErr && !!issue}
            aria-describedby="bookmark-email-help"
            onChange={(e) => setEmail(e.target.value)}
          />
          <p id="bookmark-email-help" className="text-[12px] text-muted-foreground">{GUEST.emailHelp}</p>
          {showErr && <ErrorLine>{issue}</ErrorLine>}
        </div>
        <label className="flex cursor-pointer items-start gap-3 rounded-xl border border-line-2 px-4 py-3">
          <Checkbox
            checked={consent}
            disabled={sending}
            aria-describedby="bookmark-consent-help"
            onCheckedChange={(v) => setConsent(v === true)}
            className="mt-0.5"
          />
          <span className="flex flex-col gap-1 text-[12.5px] leading-relaxed text-foreground">
            {GUEST.reminderConsent}
            <span id="bookmark-consent-help" className="text-muted-foreground">{GUEST.reminderConsentHelp}</span>
          </span>
        </label>
        <div className="flex flex-col gap-1 text-[12px] leading-relaxed text-muted-foreground">
          <p>{GUEST.bookmarkRetention(days)}</p>
          <p>
            {GUEST.privacy[0]}
            <a href={PRIVACY_URL} target="_blank" rel="noreferrer" className="font-semibold text-foreground underline underline-offset-2">
              {GUEST.privacy[1]}
            </a>
            {GUEST.privacy[2]}
          </p>
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>{GUEST.bookmarkSkip}</Button>
          <Button type="submit" disabled={sending} className="gap-1.5">
            {sending && <Loader2 className="size-4 animate-spin" />}
            {sending ? GUEST.bookmarkSending : GUEST.bookmarkSubmit}
          </Button>
        </DialogFooter>
      </form>
    )
  }

  return (
    <Dialog open={open} onOpenChange={(next) => status !== "sending" && onOpenChange(next)}>
      <DialogContent className={SHEET}>{body}</DialogContent>
    </Dialog>
  )
}
